/**
 * Trainer Action - WHAT Section
 *
 * Edit mode UI for assigning a trainer.
 * Used inline within AutomationCard when editing the "What" section.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-assign-trainer.hbs
 *
 * Fields:
 * - reminderOnId: Selected trainer ID
 * - defaultLifecycleStageId: Optional lifecycle stage to transition to
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { fetchUserQueryKey, fetchUsers } from 'src/models/user/user';
import { User, getUserDisplayName } from 'src/models/user/utils';
import { FormSelect } from 'src/shared/Forms/Fields/FormSelect';

import { ActionWhatSectionProps } from '../automationActions';
import { LifecycleTransitionField } from '../components';


// Special trainer values
const LOCATION_STAFF_VALUE = '-1';
const COMMISSION_STAFF_VALUE = '-2';

/**
 * Trainer WHAT Section - Edit mode
 */
export const TrainerWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch available trainers
  const { data: trainers = [], isLoading } = useQuery<User[]>({
    queryKey: fetchUserQueryKey({ fetchTrainers: true }),
    queryFn: () => fetchUsers({ fetchTrainers: true }),
  });

  // Build trainer options with special values
  const trainerOptions = [
    ...trainers
      .filter((t) => t.id !== 0) // Filter out id 0
      .map((t) => ({
        value: t.id.toString(),
        label: getUserDisplayName(t),
      })),
    { value: LOCATION_STAFF_VALUE, label: t('automations.trainer.staffAtClientLocation') },
  ];

  // Check if this is a purchase-based trigger (to show commission staff option)
  const isPurchaseTrigger = automation.reminderType && [
    'all', 'some', 'client', 'workout_plan', 'group', 'product', 'package'
  ].includes(automation.reminderType);

  if (isPurchaseTrigger) {
    trainerOptions.push({
      value: COMMISSION_STAFF_VALUE,
      label: t('automations.trainer.staffFromCommissionLink'),
    });
  }

  return (
    <Stack spacing={2}>
      {/* Trainer selector */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.trainer.assignTo')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <FormSelect
            name={`automations.${index}.reminderOnId`}
            options={trainerOptions}
            prompt={isLoading ? t('automations.common.loading') : t('automations.trainer.selectTrainer')}
            onChange={(e) => {
              setFieldValue(`automations.${index}.reminderOnId`, e.target.value);
              // V2 sets reminderOnType to 'User'
              setFieldValue(`automations.${index}.reminderOnType`, 'User');
            }}
          />
        </Box>
      </Box>

      {/* Lifecycle Stage Transition (Optional) */}
      <LifecycleTransitionField
        name={`automations.${index}.defaultLifecycleStageId`}
      />
    </Stack>
  );
};

/**
 * Trainer What Summary Component
 */
export const TrainerWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const { data: trainers = [] } = useQuery<User[]>({
    queryKey: fetchUserQueryKey({ fetchTrainers: true }),
    queryFn: () => fetchUsers({ fetchTrainers: true }),
  });

  if (!automation.reminderOnId) {
    return <i>{t('automations.trainer.noTrainerSelected')}</i>;
  }

  // Handle special values
  const reminderOnId = automation.reminderOnId?.toString();
  let trainerName: string;

  if (reminderOnId.toString() === LOCATION_STAFF_VALUE) {
    trainerName = t('automations.trainer.staffAtClientLocation');
  } else if (reminderOnId.toString() === COMMISSION_STAFF_VALUE) {
    trainerName = t('automations.trainer.staffFromCommissionLink');
  } else {
    const trainer = trainers.find((t) => t.id.toString() === reminderOnId.toString());
    trainerName = getUserDisplayName(trainer) || `Trainer #${automation.reminderOnId}`;
  }

  return (
    <>
      {t('automations.trainer.assign')} <b>{trainerName}</b>
    </>
  );
};
